<template>
  <div class="modal" :class="{'is-active': opened}">
    <div class="modal-background"></div>
    <div class="modal-card wide-modal">
      <header class="modal-card-head">
        <p class="modal-card-title">Freight Estimates</p>
        <button class="delete" @click="close"></button>
      </header>
      <section class="modal-card-body">
        <div v-if="error" class="notification is-danger is-light">
          <button class="delete" @click="error=''"></button>
          {{error}}
        </div>
        
        <div v-if="loading">
          <span class="icon is-medium is-size-4">
            <i class="fas fa-spinner fa-pulse"></i>
          </span>
        </div>

        <div v-if="!loading">

          <div class="field">
            <div class="control">
              <div class="select">
                <select v-model="currentPeriod">
                  <option v-for="(opt, i) in periods" :key="'period-opt-' + i">{{ opt }}</option>
                </select>
              </div>
            </div>
          </div>

          <div v-if="currentPeriod == 'New'">
            <div class="field">
              <label class="label">Start date</label>
              <div class="control">
                <input class="input" type="text" placeholder="YYYY-MM-DD" v-model="newPeriodEstimate.startDate">
              </div>
            </div>

            <div class="field">
              <label class="label">End date</label>
              <div class="control">
                <input class="input" type="text" placeholder="YYYY-MM-DD" v-model="newPeriodEstimate.endDate">
              </div>
            </div>

            <div class="field">
              <label class="label">Freight vendor</label>
              <div class="control">
                <input class="input" type="text" v-model="newPeriodEstimate.freightVendor">
              </div>
            </div>

            <div class="field">
              <label class="label">Estimates</label>
              <div class="control">
                <table class="table">
                  <thead>
                    <tr>
                      <th></th>
                      <th v-for="(loc, i) in locations" :key="'location-'+i">{{ loc }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(c, i) in countries" :key="'country-'+i">
                      <th>{{ c }}</th>
                      <td v-for="(l, j) in locations" :key="'c-' + i + '-j-' + j">
                        <input class="input" type="number" v-model.number="newPeriodEstimate.estimates[c][l]"></input>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="field is-grouped">
              <div class="control">
                <button class="button is-link" :class="{'is-loading': waiting}" :disabled="!newPeriodEstimateValid" @click="createNewPeriodEstimate">Create</button>
              </div>
            </div>
          </div>

          <div v-else>
            <div v-for="(est, i) in currentEstimates" :key="'est-' + est.id">
              <div class="">
                <table class="table">
                  <thead>
                    <tr>
                      <th>{{ est.freightVendor }}</th>
                      <th v-for="(loc, i) in locations" :key="'location1-'+i">{{ loc }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(c, i) in countries" :key="'country1-'+i">
                      <th>{{ c }}</th>
                      <td v-for="(l, j) in locations" :key="'c1-' + i + '-j1-' + j">
                        <input class="input" type="number" v-model.number="est.estimates[c][l]"></input>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="5">
                        <div class="buttons">
                          <button class="button is-link" :class="{'is-loading': waiting}" @click="saveEstimate(est)">Save</button>
                          <button class="button is-danger is-outlined" :class="{'is-loading': waiting}" @click="deleteEstimate(est)">
                            <span class="icon is-small">
                              <i class="far fa-trash-alt"></i>
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <hr />

            <div>
              <button class="button" :class="{'is-loading': waiting}" @click="newVendorOpened = !newVendorOpened">
                <span>New vendor</span>
                <span class="icon is-small">
                  <i class="fas" :class="{'fa-chevron-up': newVendorOpened, 'fa-chevron-down': !newVendorOpened}"></i>
                </span>
              </button>
            </div>
            <div v-if="newVendorOpened">
                <table class="table">
                  <thead>
                    <tr>
                      <th>
                        <input class="input" type="text" v-model="newVendorEstimate.freightVendor"></input>
                      </th>
                      <th v-for="(loc, i) in locations" :key="'location2-'+i">{{ loc }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(c, i) in countries" :key="'country2-'+i">
                      <th>{{ c }}</th>
                      <td v-for="(l, j) in locations" :key="'c2-' + i + '-j2-' + j">
                        <input class="input" type="number" v-model.number="newVendorEstimate.estimates[c][l]"></input>
                      </td>
                    </tr>
                    <tr>
                      <td colspan="5">
                        <div class="buttons">
                          <button class="button is-link" :disabled="!newVendorEstimateValid" :class="{'is-loading': waiting}" @click="createNewVendorEstimate">Create</button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>


            
          </div>

        </div>
      </section>
      <footer class="modal-card-foot">
        <a class="button" @click="close">Close</a>
      </footer>
    </div>
  </div>
</template>

<script>

const COUNTRIES = ['CHINA', 'VIETNAM']
const LOCATIONS = ['NY', 'TX', 'CA', 'ON']
const DATE_RE = /^\d{4}[\-](0[1-9]|1[012])[\-](0[1-9]|[12][0-9]|3[01])$/

function makeNewEstimates(period) {
  var startDate = ''
  var endDate = ''
  if (period && period.includes('~')) {
    var ss = period.split('~')
    startDate = ss[0].trim()
    endDate = ss[1].trim()
  }
  var estimates = {}
  for (var c of COUNTRIES) {
    estimates[c] = {}
    for (var l of LOCATIONS) {
      estimates[c][l] = 0
    }
  }
  return {
    startDate: startDate,
    endDate: endDate,
    freightVendor: '',
    estimates: estimates,
  }
}

export default {
  name: 'freight-estimates-modal',
  props: ['opened', 'pos', 'item', 'inbound'],
  data () {
    return {
      error: '',
      loading: false,
      waiting: false,
      countries: COUNTRIES,
      locations: LOCATIONS,
      allEstimates: [],
      estimatesMap: {},
      newPeriodEstimate: makeNewEstimates(),
      currentEstimates: [],
      currentPeriod: 'New',
      newVendorEstimate: null,
      newVendorOpened: false,
    }
  },
  computed: {
    server () {
      return this.$store.state.config.server
    },
    periods () {
      var periods = []
      for (var e of this.allEstimates) {
        var period = e.startDate + ' ~ ' + e.endDate
        if (!periods.includes(period)) {
           periods.push(period)
        }
      }
      periods = periods.sort((a, b) => {
        return b.localeCompare(a)
      })
      periods.unshift('New')
      return periods
    },
    newPeriodEstimateValid () {
      if (!this.newPeriodEstimate.startDate.match(DATE_RE)) {
        return false
      }
      if (!this.newPeriodEstimate.endDate.match(DATE_RE)) {
        return false
      }
      if (!this.newPeriodEstimate.freightVendor) {
        return false
      }
      return true
    },
    newVendorEstimateValid () {
      if (!this.newVendorEstimate.freightVendor) {
        return false
      }
      if (this.estimatesMap && this.currentPeriod && this.estimatesMap[this.currentPeriod]) {
        for (var v of this.estimatesMap[this.currentPeriod]) {
          if (v.freightVendor.trim() == this.newVendorEstimate.freightVendor.trim()) {
            return false
          }
        }
      }
      return true
    },
  },
  watch: {
    opened (val) {
      if (val) {
        if (!this.allEstimates.length) {
          this.getAllEstimates()
        }
      }
    },
    currentPeriod (val) {
      if (val != 'New') {
        this.currentEstimates = this.estimatesMap[val]
        this.newVendorEstimate = makeNewEstimates(val)
      }
    },
  },
  methods: {
    close () {
      this.$emit('closeFreightEstimatesModal')
    },
    getAllEstimates () {
      this.loading = true
      this.$http.get(this.server + '/myapp/get-all-freight-estimates/').then(resp => {
        this.loading = false
        this.allEstimates = resp.body
        this.$nextTick(() => {
          this.makeEstimatesMap()
        });
      }, err => {
        this.loading = false
        this.error = err.body
      })
    },
    makeEstimatesMap (period) {
      var estimatesMap = {}
      for (var e of this.allEstimates) {
        var p = e.startDate + ' ~ ' + e.endDate
        if (!estimatesMap[p]) {
          estimatesMap[p] = []
        }
        estimatesMap[p].push(e)
      }
      for (var k in estimatesMap) {
        estimatesMap[k] = estimatesMap[k].sort((a, b) => a.freightVendor.localeCompare(b.freightVendor))
      }
      this.estimatesMap = estimatesMap
      console.log('period', period)
      console.log('estimatesMap', this.estimatesMap)
      this.$nextTick(() => {
        if (period) {
          if (this.estimatesMap[period]) {
            this.currentPeriod = period
          } else {
            this.currentPeriod = this.periods[0]
          }
        } else {
          var periods = Object.keys(estimatesMap)
          periods = periods.sort((a, b) => b.localeCompare(a))
          if (periods.length) {
            this.currentPeriod = periods[0]
          }
        }
      })
    },
    createNewPeriodEstimate () {
      if (this.waiting || !this.newPeriodEstimateValid) {
        return
      }
      this.newPeriodEstimate.freightVendor = this.newPeriodEstimate.freightVendor.trim()
      this.waiting = true
      this.$http.post(this.server + '/myapp/create-freight-estimate/', this.newPeriodEstimate).then(resp => {
        var estimate = resp.body
        this.allEstimates.push(estimate)
        var period = estimate.startDate + ' ~ ' + estimate.endDate
        this.$nextTick(() => {
          this.makeEstimatesMap(period)
          this.newPeriodEstimate = makeNewEstimates()
        });
        this.waiting = false
      }, err => {
        this.waiting = false
        this.error = err.body
      })
    },
    createNewVendorEstimate () {
      if (this.waiting || !this.newVendorEstimateValid) {
        return
      }
      this.newVendorEstimate.freightVendor = this.newVendorEstimate.freightVendor.trim()
      this.waiting = true
      this.$http.post(this.server + '/myapp/create-freight-estimate/', this.newVendorEstimate).then(resp => {
        var estimate = resp.body
        this.allEstimates.push(estimate)
        this.currentEstimates.push(estimate)
        this.$nextTick(() => {
          this.makeEstimatesMap(this.currentPeriod)
          this.newVendorEstimate = makeNewEstimates(this.currentPeriod)
          this.newVendorOpened = false
        });
        this.waiting = false
      }, err => {
        this.waiting = false
        this.error = err.body
      })
    },
    saveEstimate (est) {
      if (this.waiting) {
        return
      }
      this.waiting = true
      this.$http.post(this.server + '/myapp/update-freight-estimate/', est).then(resp => {
        this.waiting = false
      }, err => {
        this.waiting = false
        this.error = err.body
      })
    },
    deleteEstimate (est) {
      if (this.waiting) {
        return
      }
      this.waiting = true
      var period = this.currentPeriod
      this.$http.post(this.server + '/myapp/delete-freight-estimate/', est).then(resp => {
        var idx = this.allEstimates.indexOf(est)
        this.allEstimates.splice(idx, 1)
        idx = this.currentEstimates.indexOf(est)
        this.currentEstimates.splice(idx, 1)
        this.$nextTick(() => {
          this.makeEstimatesMap(period)
        })
        this.waiting = false
      }, err => {
        this.waiting = false
        this.error = err.body
      })
    },
  },
}
</script>

<style scoped>
.wide-modal {
  width: 800px!important;
}
</style>